<template>
    <div class="asset-managers-dashboard">
        <!-- Sidebar KPIs -->
        <aside class="kpi-sidebar">
            <h3>{{ $t('dashboards.asset-managers.kpiTitle') }} </h3>
            <ul class="kpi-list">
                <li v-for="kpi in kpis" :key="kpi.id" :class="['kpi-item', { active: kpi.id === activeKpiId }]"
                    @click="setActiveKpi(kpi.id)" @keyup.enter="setActiveKpi(kpi.id)" role="button" tabindex="0"
                    :aria-selected="kpi.id === activeKpiId">
                    <img class="kpi-icon" :src="kpi.id === activeKpiId ? kpi.icon.active : kpi.icon.inactive"
                        :alt="kpi.name + ' icon'" />
                    <span>{{ $t(kpi.name) }}</span>
                </li>
            </ul>
        </aside>

        <!-- Main Dashboard -->
        <main class="dashboard-main-content">
            <!-- KPI 1: ESG Performance -->
            <div v-if="activeKpiId === 1" class="dashboard-view">
                <!-- ESG Performance Cards -->
                <div class="kpi-cards-grid esg-performance-cards">
                    <div class="status-card success">
                        <div class="status-header">
                            <h4>{{ $t('dashboards.asset-managers.esgInsights') }}</h4>
                            <span class="uptime-percent">{{ currentData?.esg?.overallScore?.toFixed(1) || 68.3 }}</span>
                        </div>
                        <div class="uptime-details">
                            <div class="detail-item">{{ $t('dashboards.asset-managers.esgBenchmark') }}</div>
                            <div class="detail-item trend-up">{{ $t('dashboards.asset-managers.esgTargetComparison') }}
                            </div>
                        </div>
                    </div>

                    <div class="status-card warning">
                        <div class="status-header">
                            <h4>Regional Performance {{ $t('dashboards.asset-managers.regionalPerformance') }}</h4>
                            <span class="alert-count">2 of 3</span>
                        </div>
                        <div class="maintenance-list">
                            <div class="maintenance-item">{{ $t('dashboards.asset-managers.regionalAboveBenchmark') }}
                            </div>
                            <div class="maintenance-item">{{ $t('dashboards.asset-managers.regionalImprovement') }}
                            </div>
                        </div>
                    </div>

                    <div class="status-card success">
                        <div class="status-header">
                            <h4> {{ $t('dashboards.asset-managers.fiveYearGrowth') }}</h4>
                            <span class="uptime-percent">+69%</span>
                        </div>
                        <div class="uptime-details">
                            <div class="detail-item">{{ $t('dashboards.asset-managers.fiveYearAvg') }}</div>
                            <div class="detail-item">{{ $t('dashboards.asset-managers.projection2024') }}</div>
                        </div>
                    </div>
                </div>

                <!-- ESG Performance Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.asset-managers.esgPerformanceAnalysis') }}</h4>
                            <div class="benchmark-info">
                                <span class="benchmark-text">Benchmark: 65</span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="esgBuildingChart.options" :series="esgBuildingChart.series" type="bar"
                                :height="getMainChartHeight()" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.esgRegionalPerformance') }}</h4>
                            <p class="chart-subtitle">{{ $t('dashboards.asset-managers.esgRegionalSubtitle') }}</p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="esgRadarChart.options" :series="esgRadarChart.series" type="radar"
                                :height="getMainChartHeight()" />
                        </div>
                    </div>
                </div>

                <!-- ESG Long-term Trend -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.asset-managers.esgPerformanceTrend') }}</h4>
                        <div class="chart-controls">
                            <div class="uptime-legend">
                                <span class="legend-item">
                                    <span class="legend-dot uptime"></span>
                                    <span> {{ $t('dashboards.asset-managers.esgTrendImprovement') }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart :options="esgLongTrendChart.options" :series="esgLongTrendChart.series" type="area"
                            :height="getUsageChartHeight()" />
                    </div>
                </div>
            </div>

            <!-- KPI 2: Occupancy & Revenue -->
            <div v-else-if="activeKpiId === 2" class="dashboard-view">
                <!-- Occupancy & Revenue Cards -->
                <div class="kpi-cards-grid energy-cards">
                    <div class="energy-card">
                        <div class="energy-value">{{ currentData?.occupancy?.overallRate?.toFixed(1) || 89.6 }}%</div>
                        <div class="energy-label">{{ $t('dashboards.asset-managers.occupancyRate') }}</div>
                        <div class="energy-comparison benchmark-good">
                            {{ $t('dashboards.asset-managers.occupancyGrowth') }}
                        </div>
                    </div>

                    <div class="energy-card">
                        <div class="energy-value">{{ currentData?.occupancy?.collectionRate?.toFixed(1) || 85.4 }}%
                        </div>
                        <div class="energy-label"> {{ $t('dashboards.asset-managers.collectionRate') }}</div>
                        <div class="energy-comparison trend-down">
                            {{ $t('dashboards.asset-managers.collectionEfficiency') }}
                        </div>
                    </div>

                    <div class="energy-card">
                        <div class="energy-value">{{ currentData?.occupancy?.efficiencyGap?.toFixed(1) || 4.2 }}%</div>
                        <div class="energy-label">{{ $t('dashboards.asset-managers.efficiencyGap') }}</div>
                        <div class="energy-comparison trend-up">{{
                            $t('dashboards.asset-managers.efficiencyNeedsImprovement') }}</div>
                    </div>
                </div>

                <!-- Occupancy & Revenue Analytics -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.asset-managers.occupancyVsRevenue') }}</h4>
                            <p class="chart-subtitle"> {{ $t('dashboards.asset-managers.collectionGapTracking') }}</p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="occupancyRevenueChart.options" :series="occupancyRevenueChart.series"
                                type="line" :height="getMainChartHeight()" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.asset-managers.buildingPerformanceMatrix') }}</h4>
                            <div class="benchmark-info">
                                <span class="benchmark-text">{{ $t('dashboards.asset-managers.buildingMatrixInfo') }}
                                </span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="performanceScatterChart.options"
                                :series="performanceScatterChart.series" type="bubble" :height="getMainChartHeight()" />
                        </div>
                    </div>
                </div>

                <!-- Portfolio Value Trend -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.asset-managers.portfolioValueTrend') }}</h4>
                        <div class="chart-controls">
                            <div class="occupancy-legend">
                                <span class="legend-item">
                                    <span class="legend-bar occupancy"></span>
                                    <span>{{ $t('dashboards.asset-managers.portfolioValueTotal') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-bar capacity"></span>
                                    <span>{{ $t('dashboards.asset-managers.portfolioValueGrowth') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-dot actual"></span>
                                    <span>{{ $t('dashboards.asset-managers.portfolioMarketPerformance') }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart :options="portfolioValueChart.options" :series="portfolioValueChart.series"
                            type="area" :height="getMainChartHeight()" />
                    </div>
                </div>
            </div>

            <!-- KPI 3: Market Analysis & ROI -->
            <div v-else-if="activeKpiId === 3" class="dashboard-view">
                <!-- ROI Performance Cards -->
                <div class="kpi-cards-grid-roi space-cards">
                    <div class="space-card">
                        <div class="space-value">{{ currentData?.marketRoi?.roi?.toFixed(1) || 8.7 }}%</div>
                        <div class="space-label">{{ $t('dashboards.asset-managers.averageRoi') }}</div>
                        <div class="space-target">{{ $t('dashboards.asset-managers.roiTarget') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">€{{ (currentData?.marketRoi?.portfolioValue / 1000000000)?.toFixed(1)
                            || 2.8 }}B</div>
                        <div class="space-label">{{ $t('dashboards.asset-managers.portfolioValue') }}</div>
                        <div class="space-capacity"> {{ $t('dashboards.asset-managers.portfolioYoY') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">4.2%</div>
                        <div class="space-label">{{ $t('dashboards.asset-managers.marketYield') }}</div>
                        <div class="space-action">{{ $t('dashboards.asset-managers.marketAboveAvg') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">€142M</div>
                        <div class="space-label"> {{ $t('dashboards.asset-managers.annualRevenue') }}</div>
                        <div class="space-benchmark"> {{ $t('dashboards.asset-managers.annualRevenueGrowth') }}</div>
                    </div>
                </div>

                <!-- Market Analysis Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.asset-managers.roiAnalysis') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.asset-managers.roiComparison') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="roiAnalysisChart.options" :series="roiAnalysisChart.series"
                                type="radar" :height="getMainChartHeight()" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.marketComparison') }}</h4>
                            <div class="benchmark-info">
                                <span class="benchmark-text"> {{ $t('dashboards.asset-managers.marketComparisonInfo')
                                    }}</span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="marketComparisonChart.options" :series="marketComparisonChart.series"
                                type="bar" :height="getMainChartHeight()" />
                        </div>
                    </div>
                </div>

                <!-- Asset Valuation Trends -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.asset-managers.assetValuationTrends') }}</h4>
                        <div class="chart-controls">
                            <div class="lifecycle-legend">
                                <span class="lifecycle-item"> {{ $t('dashboards.asset-managers.portfolioGrowth')
                                }}</span>
                                <span class="lifecycle-item"> {{ $t('dashboards.asset-managers.bestPerformer') }}</span>
                                <span class="lifecycle-item">{{ $t('dashboards.asset-managers.marketOutperformance')
                                }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart :options="assetValuationChart.options" :series="assetValuationChart.series"
                            type="line" :height="getUsageChartHeight()" />
                    </div>
                </div>
            </div>

            <!-- KPI 4: Tenant Experience -->
            <div v-else-if="activeKpiId === 4" class="dashboard-view">
                <!-- Tenant Satisfaction Metrics -->
                <div class="vendor-metrics-grid">
                    <div class="vendor-card">
                        <div class="vendor-value">{{ currentData?.tenantExperience?.overallSatisfaction?.toFixed(1) ||
                            4.2 }}/5</div>
                        <div class="vendor-label"> {{ $t('dashboards.asset-managers.tenantSatisfaction') }}</div>
                        <div class="vendor-trend good">{{ $t('dashboards.asset-managers.tenantSatisfactionGrowth') }}
                        </div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value">{{ currentData?.tenantExperience?.retentionRate || 94 }}%</div>
                        <div class="vendor-label">{{ $t('dashboards.asset-managers.tenantRetention') }}</div>
                        <div class="vendor-sla"> {{ $t('dashboards.asset-managers.tenantRetentionTarget') }}</div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value"> {{ $t('dashboards.asset-managers.21Days') }}</div>
                        <div class="vendor-label"> {{ $t('dashboards.asset-managers.avgResponseTime') }}</div>
                        <div class="vendor-budget"> {{ $t('dashboards.asset-managers.responseTimeTarget') }}</div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value">87%</div>
                        <div class="vendor-label"> {{ $t('dashboards.asset-managers.leaseRenewals') }}</div>
                        <div class="vendor-contracts"> {{ $t('dashboards.asset-managers.leaseRenewalsGrowth') }}</div>
                    </div>
                </div>

                <!-- Tenant Analysis Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.satisfactionByCategory') }}</h4>
                            <div class="chart-controls">
                                <div class="ratio-trend">
                                    <span class="ratio-text">{{ $t('dashboards.asset-managers.satisfactionTarget')
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="tenantSatisfactionChart.options"
                                :series="tenantSatisfactionChart.series" type="bar" :height="getMainChartHeight()" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.serviceRequestResolution') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.asset-managers.serviceResolutionInfo') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="serviceResolutionChart.options" :series="serviceResolutionChart.series"
                                type="area" :height="getMainChartHeight()" />
                        </div>
                    </div>
                </div>

                <!-- Tenant Demographics -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.asset-managers.tenantDemographics') }}</h4>
                        <div class="chart-controls">
                            <div class="uptime-legend">
                                <span class="legend-item">
                                    <span class="legend-dot uptime"></span>
                                    <span>{{ $t('dashboards.asset-managers.tenantCorporate') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-dot actual"></span>
                                    <span>{{ $t('dashboards.asset-managers.tenantSME') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-bar occupancy"></span>
                                    <span>{{ $t('dashboards.asset-managers.tenantRetail') }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart :options="tenantDemographicsChart.options" :series="tenantDemographicsChart.series"
                            type="donut" :height="getMainChartHeight()" />
                    </div>
                </div>
            </div>

            <!-- KPI 5: Financial Performance -->
            <div v-else-if="activeKpiId === 5" class="dashboard-view">
                <!-- Financial Performance Cards -->
                <div class="prediction-cards-grid">
                    <div class="prediction-card high-risk">
                        <div class="prediction-header">
                            <h4>{{ $t('dashboards.asset-managers.revenuePerformance') }}</h4>
                            <span class="risk-count">€{{ (currentData?.marketRoi?.annualRevenue / 1000000)?.toFixed(0)
                                || 142 }}M</span>
                        </div>
                        <div class="risk-list">
                            <div class="risk-item"> {{ $t('dashboards.asset-managers.annualRevenueDetails') }}</div>
                            <div class="risk-item">{{ $t('dashboards.asset-managers.q4Target') }}</div>
                            <div class="risk-item"> {{ $t('dashboards.asset-managers.noiMargin') }}</div>
                        </div>
                        <button class="action-btn"> {{ $t('dashboards.asset-managers.viewFinancialDetails') }}</button>
                    </div>

                    <div class="prediction-card budget-forecast">
                        <div class="prediction-header">
                            <h4> {{ $t('dashboards.asset-managers.costAnalysis') }}</h4>
                        </div>
                        <div class="budget-items">
                            <div class="budget-item">{{ $t('dashboards.asset-managers.operatingCosts') }}</div>
                            <div class="budget-item">{{ $t('dashboards.asset-managers.maintenanceCosts') }}</div>
                            <div class="budget-item">{{ $t('dashboards.asset-managers.capexInvestment') }}</div>
                        </div>
                        <div class="total-forecast"> {{ $t('dashboards.asset-managers.costRatio') }}</div>
                    </div>
                </div>

                <!-- Financial Analysis Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.revenueVsCosts') }}</h4>
                            <div class="alert-indicator">
                                <span class="alert-text">{{ $t('dashboards.asset-managers.noiMargin68')
                                    }}</span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="revenueAnalysisChart.options" :series="revenueAnalysisChart.series"
                                type="line" :height="getMainChartHeight()" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.asset-managers.cashFlowAnalysis') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.asset-managers.cashFlowInfo') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart :options="cashFlowChart.options" :series="cashFlowChart.series" type="bar"
                                :height="getMainChartHeight()" />
                        </div>
                    </div>
                </div>

                <!-- Financial KPIs Summary -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.asset-managers.financialMetricsDashboard')
                        }}</h4>
                        <div class="chart-controls">
                            <div class="sla-indicators">
                                <span class="sla-item good">{{ $t('dashboards.asset-managers.revenueGrowth') }}</span>
                                <span class="sla-item good"> {{ $t('dashboards.asset-managers.noiMargin68') }}</span>
                                <span class="sla-item warning">{{ $t('dashboards.asset-managers.debtServiceCoverage')
                                }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart :options="financialKpisChart.options" :series="financialKpisChart.series"
                            type="radialBar" :height="300" />
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { getAssetManagersData } from "./assetManagersData";

/*KPI icon set (active/inactive) */
import esgActive from "@/assets/client-segments/kpis-icons/esg-active.svg";
import esgInactive from "@/assets/client-segments/kpis-icons/esg-inactive.svg";
import occupancyActive from "@/assets/client-segments/kpis-icons/occupancy-active.svg";
import occupancyInactive from "@/assets/client-segments/kpis-icons/occupancy-inactive.svg";
import marketActive from "@/assets/client-segments/kpis-icons/market-active.svg";
import marketInactive from "@/assets/client-segments/kpis-icons/market-inactive.svg";
import tenantActive from "@/assets/client-segments/kpis-icons/tenant-active.svg";
import tenantInactive from "@/assets/client-segments/kpis-icons/tenant-inactive.svg";
import financialActive from "@/assets/client-segments/kpis-icons/financial-active.svg";
import financialInactive from "@/assets/client-segments/kpis-icons/financial-inactive.svg";

// Main state
const activeKpiId = ref(1);
const selectedWidget = ref(null);
const { t } = useI18n();

// Portfolio data
const portfolioData = ref(null);
const currentData = ref(null);

onMounted(() => {
    portfolioData.value = getAssetManagersData();
    currentData.value = portfolioData.value.getCurrentMonthData();
})

function formatNumber(num) {
    return num.toLocaleString('de-DE');
}

// KPI Configuration - Fixed
const kpis = ref([
    {
        id: 1,
        name: t("dashboards.asset-managers.kpi1"),
        icon: {
            active: esgActive,
            inactive: esgInactive,
        },
    },
    {
        id: 2,
        name: t("dashboards.asset-managers.kpi2"),
        icon: {
            active: occupancyActive,
            inactive: occupancyInactive,
        },
    },
    {
        id: 3,
        name: t("dashboards.asset-managers.kpi3"),
        icon: {
            active: marketActive,
            inactive: marketInactive,
        },
    },
    {
        id: 4,
        name: t("dashboards.asset-managers.kpi4"),
        icon: {
            active: tenantActive,
            inactive: tenantInactive,
        },
    },
    {
        id: 5,
        name: t("dashboards.asset-managers.kpi5"),
        icon: {
            active: financialActive,
            inactive: financialInactive,
        },
    },
]);

function setActiveKpi(id) {
    activeKpiId.value = id;
}

// Props for responsive design
const props = defineProps({
    screenWidth: {
        type: Number,
        default: typeof window !== "undefined" ? window.innerWidth : 1200,
    },
})

// Responsive chart heights
function getUsageChartHeight() {
    if (props.screenWidth <= 480) return 200;
    if (props.screenWidth <= 768) return 250;
    if (props.screenWidth <= 1024) return 300;
    return 350;
}

function getWidgetChartHeight() {
    if (props.screenWidth <= 480) return 35;
    if (props.screenWidth <= 768) return 40;
    return 45;
}

function getMainChartHeight() {
    if (props.screenWidth <= 480) return 220;
    if (props.screenWidth <= 768) return 260;
    if (props.screenWidth <= 1024) return 300;
    return 320;
}

// ESG Building Chart - Enhanced with more visual appeal
const esgBuildingChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.esgScore"),
            data: [55, 72, 78, 61, 84, 69],
        },
        {
            name: t("dashboards.asset-managers.industryBenchmark"),
            data: [65, 65, 65, 65, 65, 65],
        },
        {
            name: t("dashboards.asset-managers.targetScore"),
            data: [75, 75, 75, 75, 75, 75],
        },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#ED8936", "#6b7280", "#3b82f6"],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "65%",
                endingShape: "rounded",
                borderRadius: 6,
                dataLabels: {
                    position: "top",
                },
            },
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ["#ffffff"],
                fontSize: "11px",
                fontWeight: "bold",
            },
            offsetY: -20,
        },
        xaxis: {
            categories: [
                t("dashboards.asset-managers.towerA"),
                t("dashboards.asset-managers.towerB"),
                t("dashboards.asset-managers.towerC"),
                t("dashboards.asset-managers.retailPlaza"),
                t("dashboards.asset-managers.officeComplex"),
                t("dashboards.asset-managers.mixedUse"),
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "12px",
                },
            },
        },

        yaxis: {
            min: 0,
            max: 90,
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "12px",
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "right",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + " points";
                },
            },
        },
    },
}));

// ESG Performance Radar Chart
const esgRadarChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.currentPerformance"),
            data: [72, 68, 81, 65, 78, 71],
        },
        {
            name: t("dashboards.asset-managers.targetPerformance"),
            data: [80, 75, 85, 75, 82, 78],
        },
    ],

    options: {
        chart: {
            type: "radar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#3b82f6"],
        fill: {
            opacity: 0.15,
        },
        stroke: {
            show: true,
            width: 2,
        },
        markers: {
            size: 4,
            colors: ["#10b981", "#3b82f6"],
            strokeColors: "#fff",
            strokeWidth: 2,
        },
        xaxis: {
            categories: [
                t("dashboards.asset-managers.esgCategoryEnvironmental"),
                t("dashboards.asset-managers.esgCategorySocial"),
                t("dashboards.asset-managers.esgCategoryGovernance"),
                t("dashboards.asset-managers.esgCategoryCommunity"),
                t("dashboards.asset-managers.esgCategorySustainability"),
                t("dashboards.asset-managers.esgCategoryEthics"),
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "11.5px",
                    fontWeight: 500,
                },
                offsetY: 5,
            }
        },
        yaxis: {
            show: false,
            min: 0,
            max: 100,
        },
        plotOptions: {
            radar: {
                size: 130,
                polygons: {
                    strokeColors: '#374151',
                    strokeWidth: 1,
                    connectorColors: '#374151',
                    fill: {
                        colors: ['#232937', '#1a1f2e']
                    }
                }
            }
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "center",
            fontSize: "12px",
            offsetY: 0,
            labels: {
                colors: "#8b92a9",
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + " points";
                },
            },
        },
    },
}));
// Enhanced ESG Long Trend Chart with more data points
const esgLongTrendChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.esgScore"),
            data: [45, 48, 52, 56, 58, 62, 65, 68, 71, 74, 76, 78],
        },
        {
            name: t("dashboards.asset-managers.twoYearProjection"),
            data: [null, null, null, null, null, null, null, null, null, 74, 78, 82],
        },
    ],

    options: {
        chart: {
            type: "area",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#3b82f6"],
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: [0.7, 0.4],
                opacityTo: [0.1, 0.1],
            },
        },
        stroke: {
            curve: "smooth",
            width: [4, 3],
            dashArray: [0, 8],
        },
        xaxis: {
            categories: [
                "2020 Q1",
                "2020 Q2",
                "2021 Q1",
                "2021 Q2",
                "2022 Q1",
                "2022 Q2",
                "2023 Q1",
                "2023 Q2",
                "2024 Q1",
                "2024 Q2",
                "2025 Q1",
                "2025 Q2",
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "11px",
                },
                rotate: -45,
            },
        },
        yaxis: {
            min: 40,
            max: 85,
            labels: {
                style: { colors: "#8b92a9" },
                formatter: function (val) {
                    return val.toFixed(0);
                },
            },
        },
        markers: {
            size: [6, 4],
            colors: ["#10b981", "#3b82f6"],
            strokeColors: "#fff",
            strokeWidth: 2,
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val.toFixed(1) + " points" : null;
                },
            },
        },
    },
}));

// Enhanced Occupancy Revenue Chart with more data points
const occupancyRevenueChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.occupancyRatePercent"),
            data: [82, 85, 88, 92, 89, 94, 91, 87, 85, 88, 90, 93],
        },
        {
            name: t("dashboards.asset-managers.collectionRatePercent"),
            data: [75, 78, 82, 85, 87, 89, 88, 84, 81, 84, 86, 90],
        },
        {
            name: t("dashboards.asset-managers.targetOccupancy"),
            data: [90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90, 90],
        },
    ],

    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#ef4444", "#8b5cf6"],
        stroke: {
            curve: "smooth",
            width: [3, 3, 2],
            dashArray: [0, 0, 5],
        },
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec")
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            min: 70,
            max: 100,
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "right",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        markers: {
            size: 5,
            colors: ["#10b981", "#ef4444", "#8b5cf6"],
            strokeColors: "#fff",
            strokeWidth: 2,
            hover: {
                size: 7,
            },
        },
        tooltip: {
            theme: "dark",
            shared: true,
            intersect: false,
            y: {
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
    },
}));

// Enhanced Performance Scatter with more buildings and data - Fixed
const performanceScatterChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.portfolioPerformance"),
            data: [
                [85, 120000, 8500],
                [92, 180000, 12000],
                [78, 95000, 6200],
                [95, 165000, 9800],
                [88, 142000, 7500],
                [73, 85000, 5400],
                [96, 200000, 14500],
                [81, 105000, 6800],
            ],
        },
    ],
    options: {
        chart: {
            type: "bubble",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#8b5cf6"],
        xaxis: {
            title: {
                text: t("dashboards.asset-managers.xaxisOccupancyRate"),
                style: { color: "#8b92a9" },
            },
            labels: {
                style: { colors: "#8b92a9" },
            },
            min: 70,
            max: 100,
        },
        yaxis: {
            title: {
                text: t("dashboards.asset-managers.yaxisMonthlyRevenue"),
                style: { color: "#8b92a9" },
            },
            labels: {
                style: { colors: "#8b92a9" },
                formatter: function (val) {
                    return "€" + val / 1000 + "k";
                },
            },
        },

        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        fill: {
            opacity: 0.8,
        },
        tooltip: {
            theme: "dark",
        },
    },
}));

// Portfolio Value Trend Chart - Added
const portfolioValueChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.seriesPortfolioValue"),
            data: [2.1, 2.15, 2.22, 2.31, 2.38, 2.45, 2.51, 2.58, 2.67, 2.73, 2.76, 2.8],
        },
        {
            name: t("dashboards.asset-managers.seriesMarketIndex"),
            data: [2.1, 2.12, 2.16, 2.21, 2.25, 2.28, 2.31, 2.35, 2.41, 2.44, 2.47, 2.5],
        },
    ],

    options: {
        chart: {
            type: "area",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#8b5cf6"],
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.1,
            },
        },
        stroke: {
            curve: "smooth",
            width: [3, 2],
        },
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec")
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return "€" + val.toFixed(2) + "B";
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return "€" + val.toFixed(2) + "B";
                },
            },
        },
    },
}));

// ROI Analysis Radar Chart 
const roiAnalysisChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.seriesCurrentRoi"),
            data: [8.2, 9.1, 7.8, 8.9, 7.5, 9.4],
        },
        {
            name: t("dashboards.asset-managers.seriesTargetRoi"),
            data: [9.0, 9.5, 8.5, 9.2, 8.0, 10.0],
        },
    ],
    options: {
        chart: {
            type: "radar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#3b82f6"],
        fill: {
            opacity: 0.15,
        },
        stroke: {
            show: true,
            width: 2,
        },
        markers: {
            size: 4,
            colors: ["#10b981", "#3b82f6"],
            strokeColors: "#fff",
            strokeWidth: 2,
        },
        xaxis: {
            categories: [
                t("dashboards.asset-managers.assetOfficeTowers"),
                t("dashboards.asset-managers.assetRetailSpaces"),
                t("dashboards.asset-managers.buildingMixedUse"),
                t("dashboards.asset-managers.assetResidential"),
                t("dashboards.asset-managers.assetIndustrial"),
                t("dashboards.asset-managers.assetPremiumProperties"),
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "11.5px",
                    fontWeight: 500,
                },
                offsetY: 5,
            }
        },
        yaxis: {
            show: false,
            min: 0,
            max: 12,
        },
        plotOptions: {
            radar: {
                size: 130,
                polygons: {
                    strokeColors: '#374151',
                    strokeWidth: 1,
                    connectorColors: '#374151',
                    fill: {
                        colors: ['#232937', '#1a1f2e']
                    }
                }
            }
        },
        legend: {
            show: true,
            position: "bottom",
            horizontalAlign: "center",
            fontSize: "12px",
            offsetY: 25,
            labels: {
                colors: "#8b92a9",
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + "% ROI";
                },
            },
        },
    },
}));

// Market Comparison Chart - Added
const marketComparisonChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.ourPortfolio"),
            data: [8.7, 4.2, 12.3, 94.2, 2.1],
        },
        {
            name: t("dashboards.asset-managers.regionalAverage"),
            data: [6.2, 3.8, 8.1, 87.5, 2.8],
        },
        {
            name: t("dashboards.asset-managers.topQuartile"),
            data: [9.8, 4.8, 15.2, 96.1, 1.9],
        },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#8b5cf6", "#10b981"],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "75%",
                borderRadius: 4,
            },
        },
        xaxis: {
            categories: [
                t("dashboards.asset-managers.roi"),
                t("dashboards.asset-managers.yield"),
                t("dashboards.asset-managers.growth"),
                t("dashboards.asset-managers.occupancy"),
                t("dashboards.asset-managers.responseTime"),
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ["#ffffff"],
                fontSize: "10px",
            },
        },
        tooltip: {
            theme: "dark",
        },
    },
}));

// Asset Valuation Trends - Added
const assetValuationChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.officeTowers"),
            data: [1800, 1920, 2050, 2180, 2320],
        },
        {
            name: t("dashboards.asset-managers.retailSpaces"),
            data: [450, 465, 480, 485, 490],
        },
        {
            name: t("dashboards.asset-managers.mixedUse"),
            data: [320, 340, 365, 390, 420],
        },
        {
            name: t("dashboards.asset-managers.premiumProperties"),
            data: [280, 310, 350, 395, 450],
        },
    ],

    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#f59e0b", "#8b5cf6", "#10b981"],
        stroke: {
            curve: "smooth",
            width: 3,
        },
        xaxis: {
            categories: ["2020", "2021", "2022", "2023", "2024", "2025"],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return "€" + val + "M";
                },
            },
        },
        markers: {
            size: 5,
            strokeColors: "#fff",
            strokeWidth: 2,
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return "€" + val + "M";
                },
            },
        },
    },
}));

// Tenant Satisfaction Chart - Added
const tenantSatisfactionChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.currentScore"),
            data: [4.2, 4.5, 3.8, 4.1, 4.3, 3.9, 4.4],
        },
        {
            name: t("dashboards.asset-managers.targetScore"),
            data: [4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0],
        },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#6b7280"],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "70%",
                borderRadius: 4,
            },
        },
        xaxis: {
            xaxis: {
                categories: [
                    t("dashboards.asset-managers.maintenance"),
                    t("dashboards.asset-managers.security"),
                    t("dashboards.asset-managers.cleanliness"),
                    t("dashboards.asset-managers.communication"),
                    t("dashboards.asset-managers.amenities"),
                    t("dashboards.asset-managers.parking"),
                    t("dashboards.asset-managers.overallExperience"),
                ],
                labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
            },

            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "10px",
                },
                rotate: -45,
            },
        },
        yaxis: {
            min: 3.0,
            max: 5.0,
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return val.toFixed(1);
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ["#ffffff"],
                fontSize: "10px",
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val.toFixed(1) + "/5.0";
                },
            },
        },
    },
}));

// Service Resolution Chart - Added
const serviceResolutionChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.avgResolutionTime"),
            data: [18, 22, 16, 20, 15, 19, 17, 14, 16, 18, 15, 13],
        },
        {
            name: t("dashboards.asset-managers.firstCallResolutionRate"),
            data: [72, 75, 78, 74, 82, 79, 85, 88, 86, 84, 89, 92],
        },
    ],

    options: {
        chart: {
            type: "area",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#f59e0b", "#10b981"],
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.6,
                opacityTo: 0.1,
            },
        },
        stroke: {
            curve: "smooth",
            width: 2,
        },
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec")
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: [
            {
                title: {
                    text: t("dashboards.asset-managers.resolutionTime"),
                    style: { color: "#f59e0b" },
                },
                labels: {
                    style: { colors: "#8b92a9" },
                },
            },
            {
                opposite: true,
                title: {
                    text: t("dashboards.asset-managers.resolutionRate"),
                    style: { color: "#10b981" },
                },
                labels: {
                    style: { colors: "#8b92a9" },
                },
                min: 0,
                max: 100,
            },
        ],

        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
        },
    },
}));

// Tenant Demographics Donut Chart - Added
const tenantDemographicsChart = computed(() => ({
    series: [65, 28, 7],
    options: {
        chart: {
            type: "donut",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#8b5cf6", "#10b981"],
        labels: [
            t("dashboards.asset-managers.corporateTenants"),
            t("dashboards.asset-managers.smeBusinesses"),
            t("dashboards.asset-managers.retailServices")
        ],

        legend: {
            show: true,
            position: "bottom",
            labels: {
                colors: "#8b92a9",
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "60%",
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: t("dashboards.asset-managers.totalTenants"),
                            color: "#8b92a9",
                            formatter: function () {
                                return "247";
                            },
                        },
                    },
                },
            },

        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return Math.round(val) + "%";
            },
            style: {
                colors: ["#ffffff"],
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
    },
}));

// Revenue Analysis Chart - Added
const revenueAnalysisChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.totalRevenue"),
            data: [11.2, 11.5, 11.8, 12.1, 12.0, 12.3, 12.6, 12.4, 11.9, 12.2, 12.1, 12.5],
        },
        {
            name: t("dashboards.asset-managers.operatingCost"),
            data: [3.6, 3.7, 3.8, 3.9, 3.8, 3.9, 4.0, 3.9, 3.8, 3.9, 3.8, 3.9],
        },
        {
            name: t("dashboards.asset-managers.netOperatingIncome"),
            data: [7.6, 7.8, 8.0, 8.2, 8.2, 8.4, 8.6, 8.5, 8.1, 8.3, 8.3, 8.6],
        },
    ],

    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#ef4444", "#3b82f6"],
        stroke: {
            curve: "smooth",
            width: 3,
        },
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec")
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return "€" + val.toFixed(1) + "M";
                },
            },
        },
        markers: {
            size: 5,
            strokeColors: "#fff",
            strokeWidth: 2,
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return "€" + val.toFixed(2) + "M";
                },
            },
        },
    },
}));

// Cash Flow Chart - Added
const cashFlowChart = computed(() => ({
    series: [
        {
            name: t("dashboards.asset-managers.operatingCashFlow"),
            data: [8.2, 8.5, 8.8, 9.1, 8.9, 9.3, 9.6, 9.4, 8.9, 9.2, 9.1, 9.5],
        },
        {
            name: t("dashboards.asset-managers.capitalExpenditures"),
            data: [-1.8, -2.1, -1.6, -2.3, -1.9, -2.5, -2.8, -2.2, -1.7, -2.0, -1.8, -2.1],
        },
        {
            name: t("dashboards.asset-managers.freeCashFlow"),
            data: [6.4, 6.4, 7.2, 6.8, 7.0, 6.8, 6.8, 7.2, 7.2, 7.2, 7.3, 7.4],
        },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#ef4444", "#3b82f6"],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "75%",
                borderRadius: 4,
            },
        },
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec")
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#8b92a9",
                },
                formatter: function (val) {
                    return "€" + val.toFixed(1) + "M";
                },
            },
        },
        legend: {
            show: true,
            position: "top",
            labels: {
                colors: "#8b92a9",
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 3,
        },
        dataLabels: {
            enabled: true,
            style: {
                colors: ["#ffffff"],
                fontSize: "10px",
            },
            formatter: function (val) {
                return "€" + val.toFixed(1) + "M";
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return "€" + val.toFixed(2) + "M";
                },
            },
        },
    },
}));

// Financial KPIs Radial Chart - Added
const financialKpisChart = computed(() => ({
    series: [68.2, 81.0, 92.5, 78.3],
    options: {
        chart: {
            type: "radialBar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b"],
        labels: [
            t("dashboards.asset-managers.noiMargins"),
            t("dashboards.asset-managers.revenueGrowths"),
            t("dashboards.asset-managers.occupancyRates"),
            t("dashboards.asset-managers.costEfficiency"),
        ],

        legend: {
            show: true,
            position: "bottom",
            labels: {
                colors: "#8b92a9",
            },
        },
        plotOptions: {
            radialBar: {
                hollow: {
                    size: "35%",
                },
                track: {
                    background: "#374151",
                    strokeWidth: "97%",
                    margin: 5,
                },
                dataLabels: {
                    show: true,
                    name: {
                        fontSize: "12px",
                        color: "#8b92a9",
                        offsetY: 5,
                    },
                    value: {
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#ffffff",
                        offsetY: -10,
                        formatter: function (val) {
                            return val + "%";
                        },
                    },
                },
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
    },
}));
</script>

<style scoped>
@import "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AssetManagers.css";
</style>